/**
 * levelProgress — per-row progress for the LevelsSheet.
 *
 * Web port of client-native/src/features/home/components/parrot-levels/levelProgress.ts.
 * Each sheet row's bar is computed against ITS OWN checkType (words, reviewed
 * cards, known words, lessons, phrases, or listening time) — NOT a single
 * listening-hours fraction applied to every current row.
 */
import type { SheetLevel, ProgressCheckType } from "@/data/parrotLevels";

export interface ProgressStats {
  totalSeconds: number;
  totalWords: number;
  learningWords: number;
  reviewedCards: number;
  knownWords: number;
  completedLessons: number;
  phrases: number;
}

function getStatForCheck(
  checkType: ProgressCheckType,
  stats: ProgressStats,
): number {
  switch (checkType) {
    case "totalWords":
      return stats.totalWords;
    case "learningWords":
      return stats.learningWords;
    case "reviewedCards":
      return stats.reviewedCards;
    case "knownWords":
      return stats.knownWords;
    case "completedLessons":
      return stats.completedLessons;
    case "phrases":
      return stats.phrases;
    case "listeningTime":
      return stats.totalSeconds;
  }
}

/**
 * Compute 0..1 progress for a single level row.
 * Completed levels return 1, future levels return 0. The current (display)
 * level uses delta-based progress within its threshold range, measured against
 * the row's own checkType.
 */
export function computeLevelProgress(
  lvl: SheetLevel,
  displayLevel: number,
  stats: ProgressStats,
): number {
  if (lvl.level < displayLevel) return 1;
  if (lvl.level > displayLevel) return 0;

  const current = getStatForCheck(lvl.checkType, stats);
  const range = lvl.goalValue - lvl.prevGoalValue;
  if (range <= 0) return 1;
  return Math.max(0, Math.min(1, (current - lvl.prevGoalValue) / range));
}
